// Generic reusable data table component

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Text,
  IconButton,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Button,
  Checkbox,
} from '@chakra-ui/react';
import { MdSearch, MdChevronLeft, MdChevronRight, MdViewColumn } from 'react-icons/md';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { EmptyState } from './EmptyState';
import { LoadingSpinner } from './LoadingSpinner';
import { ThemedCheckbox } from './ThemedCheckbox';

export interface RowSelectionEvent<T> {
  rowId: string;
  checked: boolean;
  row: T;
}

export interface SelectAllEvent<T> {
  checked: boolean;
  affectedRows: T[];
}

export interface SelectionToolbarAction {
  icon: React.ReactElement;
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  isLoading?: boolean;
  emptyMessage?: string;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableColumnVisibility?: boolean;
  enableSelection?: boolean;
  initialColumnVisibility?: VisibilityState;
  selectedRows?: string[];
  pageSize?: number;
  stickyHeader?: boolean;
  maxTableHeight?: string;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T) => string;
  onRowSelectionChange?: (event: RowSelectionEvent<T>) => void;
  onSelectAllChange?: (event: SelectAllEvent<T>) => void;
  selectionToolbarActions?: SelectionToolbarAction[];
  /** Show selection toolbar even when no rows are selected (useful for unsubscribe actions) */
  alwaysShowSelectionToolbar?: boolean;
  /** Extra content rendered in the toolbar, aligned next to the search input */
  toolbarLeft?: React.ReactNode;
}

// Hook for drag-to-scroll on the table container
function useDragScroll() {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
    hasMoved: false,
  });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    dragState.current = {
      isDown: true,
      startX: e.pageX - el.offsetLeft,
      scrollLeft: el.scrollLeft,
      hasMoved: false,
    };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragState.current.isDown) return;
    const el = containerRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = x - dragState.current.startX;
    if (Math.abs(walk) > 5) {
      dragState.current.hasMoved = true;
      el.scrollLeft = dragState.current.scrollLeft - walk;
    }
  };

  const onMouseUp = () => {
    dragState.current.isDown = false;
  };

  const onMouseLeave = () => {
    dragState.current.isDown = false;
  };

  // Returns true if user was dragging, then resets the flag
  const checkAndClearDrag = () => {
    const wasDragging = dragState.current.hasMoved;
    dragState.current.hasMoved = false;
    return wasDragging;
  };

  return { containerRef, onMouseDown, onMouseMove, onMouseUp, onMouseLeave, checkAndClearDrag };
}

/**
 * Generic reusable table component using TanStack Table
 */
export function DataTable<T>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No data available',
  enableSorting = false,
  enableFiltering = false,
  enablePagination = false,
  enableColumnVisibility = false,
  enableSelection = false,
  initialColumnVisibility = {},
  selectedRows = [],
  pageSize = 10,
  stickyHeader = false,
  maxTableHeight = '500px',
  onRowClick,
  getRowId,
  onRowSelectionChange,
  onSelectAllChange,
  selectionToolbarActions = [],
  alwaysShowSelectionToolbar = false,
  toolbarLeft,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility);

  const rowGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  // Solid background for sticky header (must be opaque so rows don't bleed through)
  const stickyTheadBg = useColorModeValue('gray.100', 'gray.700');

  const dragScroll = useDragScroll();

  // Convert selectedRows array to Set for O(1) lookups (memoized to avoid recreation)
  const selectedRowIds = useMemo(() => new Set(selectedRows), [selectedRows]);

  // Helper to get row ID
  const getRowIdFn = useCallback((row: T): string => {
    if (getRowId) {
      return getRowId(row);
    }
    // Default: assume row has an 'id' property
    return (row as any).id?.toString() ?? '';
  }, [getRowId]);

  // Computed selection state
  const selectedCount = selectedRowIds.size;
  const isAllSelected = data.length > 0 && selectedCount === data.length;
  const isPartiallySelected = selectedCount > 0 && selectedCount < data.length;

  // Handle individual row selection - notify parent only (controlled component)
  const handleRowSelection = useCallback((row: T, checked: boolean) => {
    const rowId = getRowIdFn(row);
    onRowSelectionChange?.({
      rowId,
      checked,
      row,
    });
  }, [getRowIdFn, onRowSelectionChange]);

  // Handle select all - notify parent only (controlled component)
  const handleSelectAll = useCallback((checked: boolean) => {
    onSelectAllChange?.({
      checked,
      affectedRows: data,
    });
  }, [data, onSelectAllChange]);

  // Build columns with selection column prepended if enabled
  const tableColumns = useMemo(() => {
    if (!enableSelection) {
      return columns;
    }

    const selectionColumn: ColumnDef<T, any> = {
      id: '__selection',
      header: () => (
        <ThemedCheckbox
          isChecked={isAllSelected}
          isIndeterminate={isPartiallySelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          aria-label="Select all rows"
        />
      ),
      cell: ({ row }) => (
        <ThemedCheckbox
          isChecked={selectedRowIds.has(getRowIdFn(row.original))}
          onChange={(e) => {
            e.stopPropagation();
            handleRowSelection(row.original, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select row ${getRowIdFn(row.original)}`}
        />
      ),
      size: 50,
      enableSorting: false,
    };

    return [selectionColumn, ...columns];
  }, [columns, enableSelection, isAllSelected, isPartiallySelected, selectedRowIds, getRowIdFn, handleSelectAll, handleRowSelection]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    defaultColumn: { size: 150 },
    state: {
      sorting,
      globalFilter,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const hasData = data && data.length > 0;

  return (
    <Box>
      <HStack mb={4} spacing={4} justify="space-between" flexWrap="wrap">
        <HStack spacing={4}>
          {enableFiltering && (
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <MdSearch color="var(--chakra-colors-text-muted)" />
              </InputLeftElement>
              <Input
                placeholder="Search..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                bg="bg-surface"
                borderColor="border-default"
                _hover={{ borderColor: 'border-hover' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
              />
            </InputGroup>
          )}

          {toolbarLeft}

          {/* Selection toolbar actions */}
          {enableSelection && (selectedCount > 0 || alwaysShowSelectionToolbar) && selectionToolbarActions.length > 0 && (
            <HStack spacing={2}>
              <Text fontSize="sm" color="text-secondary">
                {selectedCount} selected
              </Text>
              {selectionToolbarActions.map((action, index) => (
                <Button
                  key={index}
                  leftIcon={action.isLoading ? undefined : action.icon}
                  size="sm"
                  colorScheme="brand"
                  onClick={action.onClick}
                  isLoading={action.isLoading}
                  isDisabled={action.isDisabled}
                  loadingText={action.label}
                >
                  {action.label}
                </Button>
              ))}
            </HStack>
          )}
        </HStack>

        {enableColumnVisibility && (
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              leftIcon={<MdViewColumn />}
              size="sm"
              variant="outline"
              borderColor="border-default"
              _hover={{ borderColor: 'border-hover' }}
            >
              Columns
            </MenuButton>
            {(() => {
              const visibleToggleColumns = table.getAllLeafColumns().filter(
                (col) => col.columnDef.header && col.id !== '__selection'
              );
              const allVisible = visibleToggleColumns.every((col) => col.getIsVisible());
              const someVisible = visibleToggleColumns.some((col) => col.getIsVisible());
              return (
                <MenuList
                  minW="200px"
                  maxH={visibleToggleColumns.length > 12 ? '320px' : undefined}
                  overflowY={visibleToggleColumns.length > 12 ? 'auto' : undefined}
                >
                  <MenuItem closeOnSelect={false} onClick={() => table.toggleAllColumnsVisible(!allVisible)}>
                    <Checkbox
                      isChecked={allVisible}
                      isIndeterminate={!allVisible && someVisible}
                      pointerEvents="none"
                    >
                      {allVisible ? 'Deselect all' : 'Select all'}
                    </Checkbox>
                  </MenuItem>
                  <MenuDivider m={0} />
                  {visibleToggleColumns.map((column) => (
                    <MenuItem key={column.id} closeOnSelect={false}>
                      <Checkbox
                        isChecked={column.getIsVisible()}
                        onChange={column.getToggleVisibilityHandler()}
                      >
                        {typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : column.id}
                      </Checkbox>
                    </MenuItem>
                  ))}
                </MenuList>
              );
            })()}
          </Menu>
        )}
      </HStack>

      <Box
        ref={dragScroll.containerRef as any}
        overflowX="auto"
        overflowY={stickyHeader ? 'auto' : undefined}
        maxH={stickyHeader ? maxTableHeight : undefined}
        borderWidth="1px"
        borderColor="border-default"
        borderRadius="lg"
        bg="bg-surface"
        cursor="grab"
        onMouseDown={dragScroll.onMouseDown as any}
        onMouseMove={dragScroll.onMouseMove as any}
        onMouseUp={dragScroll.onMouseUp}
        onMouseLeave={dragScroll.onMouseLeave}
        sx={{
          '&:active': { cursor: 'grabbing' },
          '&::-webkit-scrollbar': { height: '6px', width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--chakra-colors-gray-400)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--chakra-colors-gray-500)',
          },
        }}
      >
        <Table variant="simple">
          <Thead
            bg={stickyHeader ? stickyTheadBg : 'bg-muted'}
            style={stickyHeader ? { position: 'sticky', top: 0, zIndex: 1 } : undefined}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    onClick={
                      enableSorting && header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    cursor={
                      enableSorting && header.column.getCanSort()
                        ? 'pointer'
                        : 'default'
                    }
                    color="text-primary"
                    borderColor="border-default"
                    style={{
                      width: header.column.getSize(),
                      minWidth: header.column.getSize(),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {enableSorting &&
                      header.id !== '__selection' &&
                      ({
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted() as string] ?? null)}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {hasData ? (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  _hover={{ bg: onRowClick ? 'interactive-hover' : undefined }}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  onClick={() => onRowClick && !dragScroll.checkAndClearDrag() && onRowClick(row.original)}
                  bgGradient={rowGradient}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      color="text-primary"
                      borderColor="border-default"
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td
                  colSpan={table.getAllColumns().length}
                  textAlign="center"
                  py={8}
                  color="text-secondary"
                >
                  {emptyMessage}
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {enablePagination && (
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="text-secondary">
            Showing {table.getState().pagination.pageIndex * pageSize + 1} to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * pageSize,
              data.length
            )}{' '}
            of {data.length} results
          </Text>
          <HStack>
            <IconButton
              aria-label="Previous page"
              icon={<MdChevronLeft />}
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              size="sm"
            />
            <Text fontSize="sm" color="text-secondary">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </Text>
            <IconButton
              aria-label="Next page"
              icon={<MdChevronRight />}
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              size="sm"
            />
          </HStack>
        </HStack>
      )}
    </Box>
  );
}
